import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { logger } from '@/lib';
import i18n from '@/lib/i18n';
import { Button } from './Button';
import { ErrorState } from './ErrorState';

// A failed lazy route import can't be fixed by re-rendering: the chunk is gone
// (typically after a deploy), so the only remedy is a full reload. Those errors
// are rethrown to the route's errorElement (RouteErrorPage), whose button
// reloads; catching them here would offer a retry that can never succeed.
const CHUNK_IMPORT_ERROR =
  /dynamically imported module|Importing a module script failed|Loading chunk/i;

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  error: unknown;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('[ErrorBoundary]', { error, errorInfo });
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (error !== null) {
      const message = error instanceof Error ? error.message : String(error);
      if (CHUNK_IMPORT_ERROR.test(message)) {
        throw error;
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorState
          title={i18n.t('common:errors.title')}
          description={i18n.t('common:errors.description')}
          action={<Button onClick={this.handleRetry}>{i18n.t('common:errors.retry')}</Button>}
        />
      );
    }

    return this.props.children;
  }
}
